import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Flex, ScrollArea } from "@mantine/core";
import { getFilteredLayers, getVisibleLayersIds, hideLayer, reorderLayer, selectLayer, showLayer } from "../../reducers/layersReducer.js";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { Event } from "../../constants/events.js";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { LayerListHeader } from "./LayerListHeader.js";
import { LayerListFooter } from "./LayerListFooter.js";
import { LayerListItem } from "./LayerListItem.js";
import { useDragAndDrop } from "@formkit/drag-and-drop/react";
import { IconCheck } from "@tabler/icons-react";
import { PrimaryPanel } from "../../components/SidePanel.js";
export const LayerListOrdered = memo(({ selected, onClose }) => {
    const captureEvent = useCaptureEvent();
    const layers = useAppSelector(getFilteredLayers);
    const visible = useAppSelector(getVisibleLayersIds);
    const reorder = useAppAction(reorderLayer);
    const select = useAppAction(selectLayer);
    const hide = useAppAction(hideLayer);
    const show = useAppAction(showLayer);
    const visibleSet = useMemo(() => new Set(visible), [visible]);
    const onToggleVisibility = useCallback((id) => {
        if (visibleSet.has(id)) {
            captureEvent(Event.HideLayer, { id });
            hide(id);
        }
        else {
            captureEvent(Event.ShowLayer, { id });
            show(id);
        }
    }, [visibleSet, captureEvent, hide, show]);
    const options = useMemo(() => {
        return {
            onDragend(data) {
                const { initialIndex, targetIndex } = data.state;
                captureEvent(Event.ReorderLayer, {
                    from: initialIndex,
                    to: targetIndex,
                });
                reorder({
                    from: initialIndex,
                    to: targetIndex,
                });
            },
        };
    }, [captureEvent, reorder]);
    const [parent, items, setValues] = useDragAndDrop(layers, options);
    useEffect(() => {
        setValues(layers);
    }, [layers]);
    return (_jsxs(PrimaryPanel, { children: [_jsx(LayerListHeader, { onClose: onClose }), _jsx(ScrollArea, { flex: "1", offsetScrollbars: true, mr: -8, children: _jsx(Flex, { direction: "column", ref: parent, children: items.map(({ id, type }) => {
                        const onClick = () => {
                            captureEvent(Event.SelectLayer, {
                                id,
                            });
                            select(id);
                        };
                        const isSelected = id === selected?.id;
                        const isVisible = visibleSet.has(id);
                        return (_jsx(LayerListItem, { type: type, id: id, onClick: onClick, selected: isSelected, rightControl: (_jsx(IconCheck, { size: 14, cursor: "pointer", opacity: isVisible ? 1 : 0.2, onClick: (event) => {
                                    event.stopPropagation();
                                    onToggleVisibility(id);
                                } })) }, id));
                    }) }) }), _jsx(LayerListFooter, {})] }));
});
LayerListOrdered.displayName = "LayerListOrdered";
